import React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import "./styles/controlled-textarea.css";

export type ControlledTextareaProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  "control"
> &
  Omit<
    React.ComponentProps<"textarea">,
    "name" | "value" | "onChange" | "ref"
  > & {
    control: UseControllerProps<T>["control"];
    label?: string;
    helperText?: string;
  };

export function ControlledTextarea<T extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  helperText,
  ...textareaProps
}: ControlledTextareaProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({
    control: control!,
    name,
    ...(defaultValue !== undefined && { defaultValue }),
    ...(rules !== undefined && { rules }),
    ...(shouldUnregister !== undefined && { shouldUnregister }),
  });

  return (
    <div
      className={`form-field controlled-textarea ${
        error ? "form-field--error" : ""
      }`}
    >
      {label && (
        <span
          className={`form-field__label ${
            textareaProps.required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}

      <textarea
        {...textareaProps}
        {...field}
        className={`form-input form-textarea controlled-textarea__textarea ${
          textareaProps.className ?? ""
        }`}
      />

      {helperText && <span className="form-field__helper">{helperText}</span>}
      {error && <span className="form-field__error">{error.message}</span>}
    </div>
  );
}
